import React, { useState, useEffect } from 'react';
import { Smartphone, Shield, ShieldCheck, ShieldAlert, Copy, Check, X } from 'lucide-react';
import { getApiBaseUrl } from '../services/api';

const MfaSetup = ({ onCancel, onSuccess }) => {
  const [step, setStep] = useState('setup'); // setup, verify, complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secret, setSecret] = useState('');
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    initiateSetup();
  }, []);

  const initiateSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/auth/mfa/setup`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSecret(data.secret);
        setBackupCodes(data.backupCodes.split(','));

        // Fetch QR code image as blob with authorization
        try {
          const qrResponse = await fetch(`${getApiBaseUrl()}/auth/mfa/setup/qr`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          console.log('QR code response status:', qrResponse.status);

          if (qrResponse.ok) {
            const blob = await qrResponse.blob();
            console.log('QR code blob type:', blob.type, 'size:', blob.size);
            const qrCodeDataUrl = URL.createObjectURL(blob);
            console.log('QR code data URL:', qrCodeDataUrl);
            setQrCodeImageUrl(qrCodeDataUrl);
          } else {
            const errorText = await qrResponse.text();
            console.error('QR code fetch failed:', qrResponse.status, errorText);
            setError('Failed to load QR code image');
          }
        } catch (qrError) {
          console.error('QR code fetch error:', qrError);
          setError('Failed to load QR code image');
        }
      } else {
        setError(data.message || 'Failed to initiate MFA setup');
      }
    } catch (error) {
      console.error('MFA setup error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/auth/mfa/setup/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: verificationCode })
      });

      if (response.ok) {
        setStep('complete');
        if (onSuccess) onSuccess();
      } else {
        // Read response body as text first (can only read once)
        const text = await response.text();
        let errorMessage = 'Invalid verification code';
        try {
          // Try to parse as JSON
          const data = JSON.parse(text);
          errorMessage = data.message || errorMessage;
        } catch (parseError) {
          // If not JSON, use the text directly
          errorMessage = text || errorMessage;
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  if (loading && step === 'setup') {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (step === 'setup') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Smartphone className="h-5 w-5 mr-2 text-blue-600" />
            Set Up Two-Factor Authentication
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <ShieldAlert className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex justify-center mb-4">
            {qrCodeImageUrl ? (
              <img
                src={qrCodeImageUrl}
                alt="QR Code for MFA Setup"
                className="w-48 h-48"
              />
            ) : (
              <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400">Loading QR code...</span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secret Key (manual entry)
            </label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                {secret}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(secret);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            Important: Save Your Backup Codes
          </h4>
          <p className="text-sm text-yellow-700 mb-3">
            Store these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
          </p>
          <div className="bg-white border border-yellow-300 rounded p-3">
            <div className="grid grid-cols-2 gap-2 text-sm font-mono">
              {backupCodes.map((code, index) => (
                <div key={index} className="text-gray-700">{code}</div>
              ))}
            </div>
          </div>
          <button
            onClick={copyBackupCodes}
            className="mt-3 text-sm text-yellow-800 hover:text-yellow-900 flex items-center"
          >
            {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
            {copied ? 'Copied!' : 'Copy backup codes'}
          </button>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => setStep('verify')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
          >
            Continue to Verification
          </button>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="max-w-md mx-auto p-6">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <ShieldCheck className="h-5 w-5 mr-2 text-blue-600" />
            Verify Setup
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Enter the 6-digit code from your authenticator app to complete the setup.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <ShieldAlert className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleVerify}>
          <div className="mb-4">
            <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              id="verificationCode"
              type="text"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
              placeholder="000000"
              disabled={loading}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setStep('setup')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify and Enable'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
          <ShieldCheck className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Two-Factor Authentication Enabled
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Your account is now protected with two-factor authentication. You'll need to enter a code from your authenticator app each time you log in.
        </p>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
        >
          Done
        </button>
      </div>
    );
  }

  return null;
};

export default MfaSetup;
